import { FC, useEffect, useRef, useState } from "react";
import { OwcIcon } from "@one/react";
import { BOOKING_TYPE, EVENT, TIMELINE_TYPE } from "../../../constants";
import { getBookingDisplayText } from "../../../components/shared/helpFunctions";
import {
  getNameFromEmail,
  getUserDisplayById,
  getUserInitalsFromString,
} from "../../../utils/helpers/text";
import { AssistantBadge, BookerBadge } from "./components/badges";
import { useSelector } from "react-redux";
import getRRuleDisplayText from "../../../utils/helpers/text/transformRRuleToText";
import { getRepetitionIcon } from "../../../utils/helpers/getRepetitionIcon";
import moment, { Moment } from "moment";

interface UserData {
  userId: string;
  email?: string;
  givenName?: string;
  familyName?: string;
}

interface BookingApprovalInfo {
  assistent?: string | string[];
  approval?: {
    status: string;
  };
}

interface BookingData {
  bookingId?: string;
  type: string;
  fcolor?: string;
  color?: string;
  reservedForEmails?: string[];
  bookingApprovalInfo?: BookingApprovalInfo;
  createdByEmail?: string;
  createdBy?: string | {
    email?: string;
    userId?: string;
  };
  description?: string;
  assistent?: string[];
  isOngoingEventFlag?: boolean;
  isFirstTimeUser?: boolean;
  repetitionPattern: string;
  dateFrom: string;
}

interface EventData {
  id: string;
  start: string;
  end: string;
  startDate: Moment | string;
  endDate: Moment | string;
  title?: string;
  original: BookingData;
}

interface EditBookingData {
  id: string;
}

interface TimelineBookingEventProps {
  data: EventData;
  eventHover: string | null;
  setEventHover: (id: string | null) => void;
  usersList: UserData[];
  isPending: boolean;
  isNewBooking: boolean;
  isGoogleCalendarEvent: boolean;
  isEditMode: boolean;
  editBookingData: EditBookingData;
  isDuplicate: boolean;
  eventSlotDelete: (id: string) => void;
  manipulatePopoverContent: (params: {
    userSelectionType: string;
    eventId: string;
  }) => void;
  bookingPopUpDispatchAction: (action: { type: string; payload: any }) => void;
  handleOnClose: () => void;
  view: (typeof TIMELINE_TYPE)[keyof typeof TIMELINE_TYPE];
  renderBookingIcon: (
    type: string,
    bookingApprovalInfo?: BookingApprovalInfo,
  ) => string | undefined;
}

const getAssistent = (
  bookingApprovalInfo?: BookingApprovalInfo,
  bookingData?: BookingData,
): string => {
  if (
    bookingApprovalInfo?.assistent &&
    bookingApprovalInfo.assistent.length > 0
  ) {
    if (Array.isArray(bookingApprovalInfo.assistent)) {
      return bookingApprovalInfo.assistent[0];
    }
    return bookingApprovalInfo.assistent;
  }

  if (
    bookingData?.assistent &&
    Array.isArray(bookingData.assistent) &&
    bookingData.assistent[0]
  ) {
    return bookingData.assistent[0];
  }

  return "";
};

export const resolveBookerEmail = (
  bookingData?: BookingData,
  fallbackUsers: string[] = [],
): string | undefined => {
  const createdBy = bookingData?.createdBy as { email?: string } | undefined;
  return bookingData?.createdByEmail ?? createdBy?.email ?? fallbackUsers[0];
};

const COLORS = {
  PENDING_PURPLE: "#7D0096",
  PRIMARY_BLUE: "#0B41CD",
  WHITE: "#FFFFFF",
  LIGHT_BLUE_BG: "#D7E0F7",
  OFF_WHITE: "#FBFBFA",
  FIRST_TIME_BOOKING_BG: "#E4F5F1",
  EDIT_TIME_BGCOLOR: "#d7e0f7",
} as const;

const TimelineBookingEvent: FC<TimelineBookingEventProps> = ({
  data,
  eventHover,
  setEventHover,
  usersList,
  isEditMode,
  editBookingData,
  isDuplicate,
  eventSlotDelete,
  manipulatePopoverContent,
  bookingPopUpDispatchAction,
  handleOnClose,
  renderBookingIcon,
  view,
}) => {
  const bookingId = data.original?.bookingId;
  const type = data.original.type;
  const storeUser = useSelector((store: { user: { timezone: string } }) => store.user);
  const displayTimeZone = storeUser.timezone || "UTC";

  const { start, end } = data;

  const isPending =
    data.original?.bookingApprovalInfo?.approval?.status === "PENDING";
  const isNewBooking =
    (data.original.type === BOOKING_TYPE.AVAILABLE ||
      !data.original?.bookingId) &&
    data.original.type !== "GoogleCalendar";
  const isGoogleCalendarEvent = data.original.type === "GoogleCalendar";
  const isFirstTimeBooking = !!data.original?.isFirstTimeUser;
  const recurranceText = getRRuleDisplayText(data.original.repetitionPattern, new Date(data.original.dateFrom), displayTimeZone);

  const isOriginalBookingSlot = isEditMode && !isDuplicate &&
    bookingId === editBookingData.id &&
    !data.original.bookingId?.startsWith("available_");

  const fontColor = isFirstTimeBooking
    ? COLORS.PENDING_PURPLE
    : isPending
      ? COLORS.PENDING_PURPLE
      : (data.original.fcolor ?? COLORS.PRIMARY_BLUE);

  const backgroundColor = isFirstTimeBooking
    ? COLORS.FIRST_TIME_BOOKING_BG
    : isPending
      ? COLORS.WHITE
      : isOriginalBookingSlot ? COLORS.EDIT_TIME_BGCOLOR
        : (data.original.color ?? COLORS.LIGHT_BLUE_BG);

  const borderColor = isFirstTimeBooking
    ? COLORS.WHITE
    : isPending
      ? COLORS.PENDING_PURPLE
      : isEditMode
        ? (bookingId === editBookingData.id || bookingId === null) &&
        (data?.original?.fcolor ?? COLORS.PRIMARY_BLUE)
        : (type === BOOKING_TYPE.AVAILABLE && COLORS.OFF_WHITE) || "#fff";

  const reservedForEmails = data.original?.reservedForEmails || [];

  const assistantId = getAssistent(
    data?.original?.bookingApprovalInfo,
    data?.original,
  );
  const assistantDisplay = getUserDisplayById(assistantId, usersList);
  const assistantInitials =
    assistantDisplay.length > 1
      ? getUserInitalsFromString(assistantDisplay)
      : "";

  const allUsers = [...reservedForEmails];
  const uniqueUsers = [...new Set(allUsers)].filter(Boolean);
  const mainUser = uniqueUsers[0];
  const userInitials =
    mainUser?.length > 1 ? getUserInitalsFromString(mainUser) : "-";


  const isDayOr3DayView =
    view === TIMELINE_TYPE.DAY || view === TIMELINE_TYPE["3DAY"];

  const bookerEmail = resolveBookerEmail(data?.original, uniqueUsers)
  const bookerName = getNameFromEmail(bookerEmail);
  const repetitionIconStyle = { cursor: "pointer" } as const;

  const openEventPopover = () => {
    if (bookingId && !isGoogleCalendarEvent) {
      manipulatePopoverContent({
        userSelectionType: EVENT,
        eventId: data.id,
      });
    }
  };

  const formatTimeRange = (start: string, end: string) => {
    return `${start} - ${end}`;
  };

  const formatTooltipTimeRange = (startDate: Moment | string, endDate: Moment | string) => {
    const timezone = user?.timezone;
    moment.isMoment(startDate) ? startDate = startDate.format("DD-MMM-YYYY h:mmA") : startDate = moment.tz(new Date(startDate), timezone).format("DD-MMM-YYYY h:mmA");
    moment.isMoment(endDate) ? endDate = endDate.format("DD-MMM-YYYY h:mmA") : endDate = moment.tz(new Date(endDate), timezone).format("DD-MMM-YYYY h:mmA");
    return `${startDate} - ${endDate}`;
  };

  const eventRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 16, y: 52 });
  const [hasEnoughWidth, setHasEnoughWidth] = useState(false);
  const [shouldAllowSpillover, setShouldAllowSpillover] = useState(true);
  const [stickyOffset, setStickyOffset] = useState(0);
  const { isTvMode, user } = useSelector(
    (state: any) => ({ isTvMode: state?.bookingInstruments?.isTvMode, user: state?.user }),
  );
  useEffect(() => {
    const element = eventRef.current?.parentElement;

    if (!element) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const width = entry.contentRect.width;
        setHasEnoughWidth(width >= 36);
      }
    });

    resizeObserver.observe(element);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    const element = eventRef.current?.parentElement;
    if (!element || isNewBooking) return;

    const calculateSpillover = () => {
      const timelineRow = element.closest(".mbsc-timeline-row");
      if (!timelineRow) {
        return;
      }

      const allBookingsInRow = timelineRow.querySelectorAll(
        ".mbsc-schedule-event",
      );
      const currentRect = element.getBoundingClientRect();
      const currentLeft = currentRect.left;
      const currentWidth = currentRect.width;
      if (currentWidth >= 189) {
        setShouldAllowSpillover(false);
      }
      const minSpilloverDistance = Math.max(currentWidth, 189);

      let hasEnoughSpace = true;
      let minDistance = Infinity;

      allBookingsInRow.forEach((siblingBooking) => {
        if (siblingBooking === element) return;

        const siblingRect = siblingBooking.getBoundingClientRect();
        const siblingLeft = siblingRect.left;
        const distanceToSibling = siblingLeft - currentLeft;

        if (distanceToSibling > 0 && distanceToSibling < minDistance) {
          minDistance = distanceToSibling;
        }

        if (distanceToSibling > 0 && distanceToSibling < minSpilloverDistance) {
          hasEnoughSpace = false;
        }
      });

      setShouldAllowSpillover(hasEnoughSpace);
    };

    calculateSpillover();

    window.addEventListener("resize", calculateSpillover);

    const timelineRow = element.closest(".mbsc-timeline-row");
    if (timelineRow) {
      const observer = new MutationObserver(calculateSpillover);
      observer.observe(timelineRow, {
        childList: true,
        subtree: true,
      });

      return () => {
        window.removeEventListener("resize", calculateSpillover);
        observer.disconnect();
      };
    }

    return () => {
      window.removeEventListener("resize", calculateSpillover);
    };
  }, [data.start, data.end, data.id, bookingId, isNewBooking]);

  useEffect(() => {
    const element = eventRef.current?.parentElement;
    if (!element || isNewBooking) return;

    const scrollContainer = element.closest(".mbsc-timeline-grid-scroll");
    if (!scrollContainer) return;

    const timeline = element.closest(".mbsc-timeline");
    const resourceSidebar = timeline?.querySelector(".mbsc-timeline-resources");
    const resourceWidth = resourceSidebar
      ? (resourceSidebar as HTMLElement).clientWidth
      : 0;

    let rafId: number | null = null;

    const handleScroll = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }

      rafId = requestAnimationFrame(() => {
        const bookingRect = element.getBoundingClientRect();
        const containerRect = scrollContainer.getBoundingClientRect();

        const bookingLeftRelativeToContainer =
          bookingRect.left - containerRect.left;
        const bookingWidth = bookingRect.width;

        if (bookingLeftRelativeToContainer < resourceWidth) {
          const scrolledUnderSidebar =
            resourceWidth - bookingLeftRelativeToContainer;
          const minContentWidth =
            shouldAllowSpillover && bookingWidth < 183 ? 0 : 183;
          const maxOffset = Math.max(0, bookingWidth - 36 - minContentWidth);
          const offset = Math.min(scrolledUnderSidebar, maxOffset);
          setStickyOffset(offset);
        } else {
          setStickyOffset(0);
        }
      });
    };

    scrollContainer.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [isNewBooking, shouldAllowSpillover]);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (eventHover === data.id) {
      const rect = e.currentTarget.getBoundingClientRect();
      const offsetX = 10;

      const element = eventRef.current?.parentElement;
      const scrollContainer = element?.closest(
        ".mbsc-timeline-grid-scroll",
      ) as HTMLElement;

      const tooltipHeight =
        tooltipRef.current?.offsetHeight || 150;
      const eventHeight = rect.height;

      let showOnTop = false;
      if (scrollContainer) {
        const containerRect = scrollContainer.getBoundingClientRect();
        const spaceBelow = containerRect.bottom - rect.bottom;
        showOnTop = spaceBelow < tooltipHeight;
      }

      setTooltipPosition({
        x: e.clientX - rect.left + offsetX,
        y: showOnTop
          ? -(tooltipHeight - eventHeight) - 46
          : eventHeight + 16,
      });
    }
  };

  const renderDayOr3DayViewContent = () => (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "36px",
        justifyContent: assistantDisplay ? "flex-start" : "center",
        alignItems: assistantDisplay ? "flex-start" : "center",
        width: "100%",
      }}
    >
      <div
        style={{
          borderRadius: "0.25rem",
          display: "flex",
          alignItems: "center",
          height: "18px",
        }}
      >
        <span
          style={{
            display: "flex",
            gap: "4px",
            textOverflow: "ellipsis",
            fontFamily: "Roche Sans",
            fontSize: "0.75rem",
            fontStyle: "normal",
            fontWeight: "400",
            whiteSpace: "nowrap",
            cursor: 'pointer'
          }}
        >
          {recurranceText && (
            <>
              {getRepetitionIcon(recurranceText, repetitionIconStyle)}
            </>
          )}
          {bookerName}
        </span>
      </div>
      {assistantDisplay && (
        <div
          style={{
            borderRadius: "0.25rem",
            maxWidth: "100%",
            background: "#fff",
            display: "flex",
            alignItems: "center",
            height: "18px",
          }}
        >
          <span
            style={{
              color: "#7D0096",
              textOverflow: "ellipsis",
              fontFamily: "Roche Sans",
              fontSize: "0.75rem",
              fontStyle: "normal",
              fontWeight: "400",
              lineHeight: "18px",
              whiteSpace: "nowrap",
            }}
          >
            {assistantDisplay}
          </span>
        </div>
      )}
    </div>
  );

  const renderNewBookingContent = () => (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "36px",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
      }}
    >
      <div
        style={{
          borderRadius: "0.25rem",
          display: "flex",
          alignItems: "center",
          height: "18px",
        }}
      >
        <span
          style={{
            textOverflow: "ellipsis",
            fontFamily: "Roche Sans",
            fontSize: "0.75rem",
            fontStyle: "normal",
            fontWeight: "400",
            lineHeight: "18px",
            whiteSpace: "nowrap",
          }}
        >
          {formatTimeRange(start, end)}
        </span>
      </div>
    </div>
  );

  const renderDefaultContent = () => (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        height: "36px",
        alignItems: 'center',
        gap: '4px',
        width: "100%",
      }}
    >
      {recurranceText && (
        <>
          {getRepetitionIcon(recurranceText, repetitionIconStyle)}
        </>
      )}
      <div style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        alignItems: "flex-start",
      }}>
        <div
          style={{
            borderRadius: "0.25rem",
            display: "flex",
            alignItems: "center",
            height: "18px",
            maxWidth: "100%",
          }}
        >
          <span
            style={{
              textOverflow: "ellipsis",
              fontFamily: "Roche Sans",
              fontSize: "0.75rem",
              fontStyle: "normal",
              fontWeight: "400",
              lineHeight: "18px",
              whiteSpace: "nowrap",
            }}
          >
            {bookerName}
          </span>
        </div>
        <div
          style={{
            borderRadius: "0.25rem",
            display: "flex",
            alignItems: "center",
            height: "18px",
          }}
        >
          <span
            style={{
              textOverflow: "ellipsis",
              fontFamily: "Roche Sans",
              fontSize: "0.75rem",
              fontStyle: "normal",
              fontWeight: "400",
              lineHeight: "18px",
              whiteSpace: "nowrap",
            }}
          >
            {formatTimeRange(start, end)}
          </span>
        </div>
      </div>
    </div>
  );

  const renderBookingContent = () => {
    if (isDayOr3DayView && !isNewBooking) {
      return renderDayOr3DayViewContent();
    }
    if (isNewBooking) {
      return renderNewBookingContent();
    }
    return renderDefaultContent();
  };

  return (
    <div
      ref={eventRef}
      className={`md-timeline-template-event ${isPending ? "pending-booking" : ""} ${isGoogleCalendarEvent ? "google-calendar-event" : ""} ${isNewBooking ? "new-booking" : ""}`}
      style={{
        boxSizing: "content-box",
        cursor: 'pointer',
        minWidth:
          !isNewBooking && !isEditMode && eventHover === data.id
            ? "189px"
            : "auto",
      }}
      onMouseEnter={() => setEventHover(data.id)}
      onMouseLeave={() => setEventHover(null)}
      onMouseMove={handleMouseMove}
    >
      {!isGoogleCalendarEvent && (
        <div
          ref={tooltipRef}
          className="md-timeline-tooltip-single"
          style={{
            position: "absolute",
            top: `${tooltipPosition.y}px`,
            left: `${tooltipPosition.x}px`,
            height: "fit-content",
            width: "max-content",
            borderRadius: "4px",
            backgroundColor: "#F5F5F2",
            padding: "12px",
            gap: "4px",
            display: !isNewBooking && eventHover === data.id ? "flex" : "none",
            flexDirection: "row",
            boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.15)",
          }}
        >
          <div
            style={{
              width: "24px",
              height: "24px",
              zIndex: 999999,
              position: "relative",
              borderRadius: "4px",
              border: "1px solid #FBFBFA",
              backgroundColor: backgroundColor,
              marginTop: '3px'
            }}
          >
            <div
              style={{
                width: "24px",
                height: "24px",
                borderRadius: "5px",
                backgroundColor: "rgba(0, 0, 0, 0.05)",
                position: "absolute",
                top: "1px",
              }}
            ></div>

            <OwcIcon
              className="one-icons-outlined"
              style={{
                fontSize: "16px",
                width: "24px",
                height: "24px",
                lineHeight: "24px",
                textAlign: "center",
                color: fontColor,
                border: borderColor,
              }}
              key={data.id}
              name={
                renderBookingIcon(
                  type,
                  data?.original?.bookingApprovalInfo,
                ) as any
              }
              placeholder={undefined}
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            />
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              color: "rgba(33, 32, 31, 1)",
            }}
          >
            <span
              style={{
                fontSize: "12px",
              }}
            >
              <span
                style={{
                  fontWeight: "bold",
                }}
              >
                {getBookingDisplayText(data?.original?.bookingApprovalInfo)}{" "}
                {"by: "}
              </span>
              {bookerName}
            </span>

            <span
              style={{
                fontSize: "12px",
              }}
            >
              <span
                style={{
                  fontWeight: "bold",
                }}
              >
                {"Booking for: "}
              </span>
              {uniqueUsers.length > 1
                ? uniqueUsers.map((user) => getNameFromEmail(user)).join(", ")
                : getNameFromEmail(uniqueUsers[0])}
            </span>

            <span
              style={{
                fontSize: "12px",
              }}
            >
              <span
                style={{
                  fontWeight: "bold",
                }}
              >
                {"Duration: "}
              </span>
              {formatTooltipTimeRange(data.startDate, data.endDate)}
            </span>

            <span
              style={{
                fontSize: "12px",
              }}
            >
              <span
                style={{
                  fontWeight: "bold",
                }}
              >
                {"Description: "}
              </span>
              {data.original?.description || "-"}
            </span>

            {assistantInitials && (
              <span
                style={{
                  fontSize: "12px",
                }}
              >
                <span
                  style={{
                    fontWeight: "bold",
                  }}
                >
                  {"Assistant: "}
                </span>
                {assistantDisplay}
              </span>
            )}
          </div>
        </div>
      )}

      <div
        className="md-timeline-template-event-cont"
        style={{
          borderColor: borderColor || "#fff",
          background: backgroundColor,
          boxShadow: isPending
            ? "0px 2px 4px 0px rgba(124, 58, 237, 0.25)"
            : "0px 1px 3px 0px rgba(0, 0, 0, 0.40)",
          borderStyle: "solid",
          borderWidth: "1px",
          overflow: shouldAllowSpillover ? "visible" : "hidden",
          boxSizing: "content-box",
          width: "auto",
          minWidth:
            !isNewBooking && !isEditMode && eventHover === data.id
              ? "189px"
              : "inherit",
        }}
        onClick={openEventPopover}
      >
        {isGoogleCalendarEvent ? (
          <>
            <span
              className="md-timeline-template-title"
              style={{
                color: fontColor,
                fontWeight: "bold",
                marginLeft: "4px",
                display: "block",
              }}
            >
              {data.title}
            </span>
            <span
              className="md-timeline-template-time"
              style={{ color: fontColor }}
            >
              {formatTimeRange(start, end)}
            </span>
          </>
        ) : (
          <>
            {!isNewBooking && (
              <div
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  height: "36px",
                  width: "100%",
                  background: backgroundColor,
                  borderRadius: "7px",
                }}
              />
            )}
            {!isNewBooking && (
              <div
                className="md-timeline-template-event-type"
                style={{
                  overflow: "hidden",
                  position: "absolute",
                  left: `${stickyOffset}px`,
                  top: 0,
                  height: "36px",
                  width: "100%",
                  maxWidth: "36px",
                }}
              >
                <div
                  style={{
                    minWidth: "12px",
                    height: "36px",
                    width: "100%",
                    maxWidth: "36px",
                    boxSizing: "border-box",
                    position: "relative",
                    borderRadius: "7px",
                    borderRight: hasEnoughWidth
                      ? `1px solid ${borderColor}`
                      : "none",
                  }}
                >
                  <OwcIcon
                    className="one-icons-outlined"
                    style={{
                      position: "absolute",
                      top: "5px",
                      fontSize: "20px",
                      left: "8px",
                      width: "20px",
                      height: "20px",
                      color: fontColor,
                      border: borderColor,
                      cursor: "pointer"
                    }}
                    key={data.id}
                    name={
                      (eventHover &&
                        data.id === eventHover &&
                        (isEditMode
                          ? bookingId === editBookingData.id || bookingId === null
                          : bookingId === null) &&
                        !(isDuplicate && !data.id?.includes("available_"))
                        ? "clear"
                        : renderBookingIcon(
                          type,
                          data?.original?.bookingApprovalInfo,
                        )) as any
                    }
                    onClick={(ev) => {
                      if (isEditMode) {
                        if (data.original.isOngoingEventFlag) {
                          bookingPopUpDispatchAction({
                            type: "cancelEvents",
                            payload: {
                              onApprove: handleOnClose,
                            },
                          });
                        } else {
                          if (
                            bookingId === editBookingData.id ||
                            bookingId === null
                          ) {
                            if (
                              !(isDuplicate && !data.id?.includes("available_"))
                            ) {
                              eventSlotDelete(data.id);
                            }
                          }
                        }
                      } else {
                        if (bookingId === null) {
                          eventSlotDelete(data.id);
                        }else if (bookingId && !isGoogleCalendarEvent) {
                          openEventPopover();
                        }
                      }
                      ev.stopPropagation();
                    }}
                    placeholder={undefined}
                    onPointerEnterCapture={undefined}
                    onPointerLeaveCapture={undefined}
                  />
                  {assistantInitials && (
                    <AssistantBadge
                      initials={assistantInitials}
                      displayName={assistantDisplay}
                      isPending={isPending}
                    />
                  )}

                  {mainUser && (
                    <BookerBadge
                      initials={userInitials}
                      hasMultipleUsers={uniqueUsers.length > 1}
                      isPending={isPending}
                      fontColor={fontColor}
                      isFirstTimeBooking={
                        data.original?.isFirstTimeUser || false
                      }
                    />
                  )}
                </div>
              </div>
            )}

            {isNewBooking && (
              <OwcIcon
                className="one-icons-outlined"
                style={{
                  position: "absolute",
                  fontSize: "20px",
                  left: "8px",
                  width: "20px",
                  height: "20px",
                  color: fontColor,
                  border: borderColor,
                  cursor: "pointer"
                }}
                key={data.id}
                name={
                  (eventHover &&
                    data.id === eventHover &&
                    (isEditMode
                      ? bookingId === editBookingData.id || bookingId === null
                      : bookingId === null)
                    ? "clear"
                    : renderBookingIcon(
                      type,
                      data?.original?.bookingApprovalInfo,
                    )) as any
                }
                onClick={(ev) => {
                  if (isEditMode) {
                    if (data.original.isOngoingEventFlag) {
                      bookingPopUpDispatchAction({
                        type: "cancelEvents",
                        payload: {
                          onApprove: handleOnClose,
                        },
                      });
                    } else {
                      if (
                        bookingId === editBookingData.id ||
                        bookingId === null
                      ) {
                        eventSlotDelete(data.id);
                      }
                    }
                     ev.stopPropagation();
                  } else {
                    if (bookingId === null) {
                      eventSlotDelete(data.id);
                    }
                  }
                }}
                placeholder={undefined}
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
            )}
            <div
              className={`booking-title-container ${isTvMode ? "booking-title-container--tv-mode" : ""}`}
              style={{
                position: "absolute",
                left: `${36 + stickyOffset}px`,
                marginLeft: "4px",
                color: fontColor,
                whiteSpace: "nowrap",
                overflow: shouldAllowSpillover ? "visible" : "hidden",
                direction: "ltr",
                textOverflow: "ellipsis",
                background: "none",
              }}
            >
              {renderBookingContent()}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TimelineBookingEvent;
